import { Component, signal, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MsfInput } from '@components/ui/forms/input/input';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { SystemDocumentationBlock } from '@6grain/masofa-monolith-api-client/dictionaries';
import { SystemDocumentationBlockService } from '@6grain/masofa-monolith-api-client/dictionaries/api/systemDocumentationBlock.service';
import { LocalizationString } from '@6grain/masofa-monolith-api-client/common';
import { Toast } from 'primeng/toast';
import { PrimaryButton } from '@components/button/primary-button/primary-button';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { CommonModule } from '@angular/common';
import { MsfSelectPaginated } from '@components/select/paginated/select-paginated';
import { FilterOperator } from '@app/utils/filter-operator';

interface SystemDocumentationBlockFormData extends SystemDocumentationBlock {
  names: LocalizationString;
  parentId?: string | null;
  orderCode?: number | null;
}

@Component({
  selector: 'app-add-system-documentation-block-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    MsfInput,
    Toast,
    PrimaryButton,
    SecondaryButton,
    MsfSelectPaginated
  ],
  providers: [MessageService, DialogService],
  templateUrl: './add-system-documentation-block-form.html',
  styleUrl: './add-system-documentation-block-form.scss'
})
export class AddSystemDocumentationBlockForm implements OnInit {
  isSaving = signal(false);
  isCanceling = signal(false);

  formData: SystemDocumentationBlockFormData = {
    id: '',
    status: 0,
    names: {
      'ru-RU': '',
      'en-US': '',
      'uz-Latn-UZ': ''
    },
    parentId: null,
    orderCode: null
  };

  mode: 'create' | 'edit' = 'create';

  parentBlockFilter: Array<{ filterField: string; filterOperator: FilterOperator; filterValue: any }> = [];

  // Track field errors
  fieldErrors = {
    names: false
  };

  constructor(
    public tr: TranslateService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public systemDocumentationBlockService: SystemDocumentationBlockService,
    private messageService: MessageService
  ) {
  }

  ngOnInit() {
    if (this.config.data) {
      this.mode = this.config.data.mode || 'create';

      if (this.mode === 'edit' && this.config.data.model) {
        this.formData = {
          ...this.formData,
          ...this.config.data.model
        };
      }
    }

    if (this.mode === 'edit' && this.formData.id) {
      this.parentBlockFilter = [{
        filterField: 'id',
        filterOperator: FilterOperator.NotEquals,
        filterValue: this.formData.id
      }];
    }
  }

  saveSystemDocumentationBlock(): void {
    this.validateFields();

    if (!this.isFormValid()) {
      const missingFields: string[] = [];

      if (this.fieldErrors.names) {
        missingFields.push(this.tr.instant('Name'));
      }

      const detail = this.tr.instant('Please fill the following required fields') + ': ' + missingFields.join(', ');

      this.messageService.add({
        severity: 'error',
        summary: this.tr.instant('Validation Error'),
        detail: detail,
        life: 5000
      });
      return;
    }

    this.isSaving.set(true);

    // Prepare data for API - convert empty parentId to null
    const blockData: SystemDocumentationBlock = {
      ...this.formData,
      parentId: (this.formData.parentId && typeof this.formData.parentId === 'string' && this.formData.parentId.trim() !== '') 
        ? this.formData.parentId 
        : null
    };

    // Remove id when creating new block - API will generate it
    if (this.mode === 'create') {
      delete (blockData as any).id;
    }

    if (this.mode === 'create') {
      this.systemDocumentationBlockService
        .dictionariesSystemDocumentationBlockCreatePost(blockData)
        .subscribe({
          next: (response: string) => {
            this.isSaving.set(false);
            this.showSuccessMessage('System documentation block created successfully');
            this.ref.close({ success: true, data: response });
          },
          error: (error: any) => {
            this.handleError(error);
          }
        });
    } else {
      this.systemDocumentationBlockService
        .dictionariesSystemDocumentationBlockUpdatePut(blockData)
        .subscribe({
          next: (response: SystemDocumentationBlock) => {
            this.isSaving.set(false);
            this.showSuccessMessage('System documentation block updated successfully');
            this.ref.close({ success: true, data: response });
          },
          error: (error: any) => {
            this.handleError(error);
          }
        });
    }
  }

  cancelForm(): void {
    this.isCanceling.set(true);
    this.ref.close({ success: false });
  }

  isFormValid(): boolean {
    return !!(
      this.formData.names['uz-Latn-UZ']?.trim() ||
      this.formData.names['en-US']?.trim() ||
      this.formData.names['ru-RU']?.trim()
    );
  }

  validateFields(): void {
    this.fieldErrors.names = !this.isFormValid();
  }

  onNameChange(): void {
    if (this.fieldErrors.names) {
      this.fieldErrors.names = !this.isFormValid();
    }
  }

  private showSuccessMessage(detail: string): void {
    this.messageService.add({
      severity: 'success',
      summary: this.tr.instant('Success'),
      detail: this.tr.instant(detail),
      life: 3000
    });
  }

  private handleError(error: any): void {
    this.isSaving.set(false);
    
    // Extract validation errors from response
    let errorMessage = 'An error occurred';
    
    if (error?.error) {
      const errorObj = error.error;
      
      // Handle validation errors
      if (errorObj.errors) {
        const validationErrors: string[] = [];
        
        // Collect all validation error messages
        Object.keys(errorObj.errors).forEach(key => {
          const messages = errorObj.errors[key];
          if (Array.isArray(messages)) {
            messages.forEach((msg: string) => validationErrors.push(msg));
          } else {
            validationErrors.push(messages);
          }
        });
        
        if (validationErrors.length > 0) {
          errorMessage = validationErrors.join(', ');
        } else {
          errorMessage = errorObj.title || errorObj.message || errorMessage;
        }
      } else if (errorObj.message) {
        errorMessage = errorObj.message;
      } else if (errorObj.title) {
        errorMessage = errorObj.title;
      }
    } else if (error?.message) {
      errorMessage = error.message;
    }
    
    this.messageService.add({
      severity: 'error',
      summary: this.tr.instant('Error'),
      detail: errorMessage,
      life: 5000
    });
  }
}

